import React from "react";

import type { ACPConsentVersionRow } from "@/features/acp/helpers/consent-versions";
import { cn } from "@/lib/helpers/cn";
import dayjs, { guessTimezone } from "@/lib/helpers/dayjs";
import type { UUID } from "@/core/types";

import { DetailItem } from "./SectionDetail";
import { DownloadButton } from "./DownloadButton";
import { MemberProfileAcpBadge } from "./MemberProfileAcpBadge";

const EM_DASH = "—";

// Desktop column widths/order shared with SectionDetailListHeader — the header renders once
// above the list instead of repeating these labels per row, so both must stay aligned.
export const ACP_DESKTOP_GRID_CLASSES =
  "tw:lg2:grid-flow-col tw:lg2:grid-cols-[1.5fr_repeat(4,minmax(140px,1fr))_0.5fr] tw:lg2:items-center tw:lg2:gap-x-3";

// Single source of column order/labels — SectionDetailListHeader renders these once instead of
// buildDetails repeating them per row.
const COLUMN_KEYS = [
  "consent_status",
  "version",
  "given_by",
  "timestamp",
  "effective_date",
  "download",
] as const;

const COLUMN_LABELS: Record<(typeof COLUMN_KEYS)[number], string> = {
  consent_status: "Consent Status",
  download: "",
  effective_date: "Effective Date",
  given_by: "Given By",
  timestamp: "Response Date",
  version: "Version",
};

export const ACP_COLUMN_LABELS = COLUMN_KEYS.map((key) => COLUMN_LABELS[key]);

type SectionDetailListProps = {
  patientId: UUID;
  row: ACPConsentVersionRow;
};

export function SectionDetailList({ patientId, row }: SectionDetailListProps) {
  const details = buildDetails({ patientId, row });

  return (
    <dl
      className={cn(
        "tw:m-0! tw:grid tw:w-full",
        "tw:max-lg2:grid-cols-2 tw:max-lg2:gap-y-2",
        ACP_DESKTOP_GRID_CLASSES,
      )}
    >
      {details.map((detail) => (
        <DetailItem detail={detail} hideLabelAtDesktop key={detail.key} />
      ))}
    </dl>
  );
}

// Old and updated consent rows share the exact same column set — unanswered versions render
// dashes for the response-derived columns instead of hiding/reshaping the row. The download
// action stays visible but disabled (with an explanatory tooltip) rather than dashed out, since
// it's an action rather than a data point.
function buildDetails({
  patientId,
  row: { consent, consentStatus, version },
}: {
  patientId: UUID;
  row: ACPConsentVersionRow;
}) {
  const hasResponse = consentStatus === "OPTED_IN" || consentStatus === "OPTED_OUT";

  const { consentFormEffectiveDate, entityId, responseGivenByName, timestamp, userFileReportKey } =
    consent || {};

  return [
    {
      key: "consent_status",
      label: COLUMN_LABELS.consent_status,
      value: <MemberProfileAcpBadge status={consentStatus} />,
    },
    { key: "version", label: COLUMN_LABELS.version, value: `v${version}` },
    {
      key: "given_by",
      label: COLUMN_LABELS.given_by,
      value: hasResponse ? responseGivenByName : EM_DASH,
    },
    {
      key: "timestamp",
      label: COLUMN_LABELS.timestamp,
      value: hasResponse ? formatConsentDate(timestamp) : EM_DASH,
    },
    {
      key: "effective_date",
      label: COLUMN_LABELS.effective_date,
      value: hasResponse ? consentFormEffectiveDate : EM_DASH,
    },
    {
      key: "download",
      label: COLUMN_LABELS.download,
      value: (
        <DownloadButton
          consentStatus={consentStatus}
          key="download"
          patientId={entityId ?? patientId}
          reportKey={hasResponse ? userFileReportKey : undefined}
        />
      ),
    },
  ];
}

function formatConsentDate(timestamp?: string) {
  if (!timestamp) return EM_DASH;

  return dayjs.utc(timestamp).tz(guessTimezone()).format("M/D/YYYY hh:mm a");
}
